import { parseUserIdFromRef } from './checklistEventAssignment';

const parseNumericIdFromTail = (raw: string): number | null => {
	const match = raw.match(/(\d+)$/);
	if (match) return Number(match[1]);
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
};

export function parseGroupIdFromRef(value: unknown): number | null {
	if (value == null) return null;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') return parseNumericIdFromTail(value);
	if (typeof value === 'object') {
		const o = value as Record<string, unknown>;
		if (o.id != null) return parseGroupIdFromRef(o.id);
		if (typeof o['@id'] === 'string') return parseNumericIdFromTail(o['@id']);
	}
	return null;
}

export function parseSecurityRoleIdFromRef(value: unknown): number | null {
	return parseGroupIdFromRef(value);
}

export function extractGroupMembers(group: unknown): unknown[] {
	if (!group || typeof group !== 'object') return [];
	const g = group as Record<string, unknown>;
	if (Array.isArray(g.checklistGroupMembers)) return g.checklistGroupMembers;
	if (Array.isArray(g.checklist_group_members)) return g.checklist_group_members;
	return [];
}

const resolveMemberUserId = (member: unknown): number | null => {
	if (!member || typeof member !== 'object') return parseUserIdFromRef(member);
	const m = member as Record<string, unknown>;
	return parseUserIdFromRef(m.user ?? m.User ?? member);
};

/**
 * Returns true when the user belongs to an assignment group (checklist_groups),
 * either as an explicit member or via a matching security role on role-based groups.
 */
export function isUserMemberOfAssignmentGroup(
	group: unknown,
	currentUserId: number | null,
	currentSecurityRoleId: number | null
): boolean {
	if (currentUserId == null || !group || typeof group !== 'object') return false;

	const g = group as Record<string, unknown>;
	const members = extractGroupMembers(g);
	const groupType = g.type ?? g.assignmentType;

	if (members.length > 0 || groupType === 'users' || groupType === 'user') {
		return members.some((member) => {
			const memberUserId = resolveMemberUserId(member);
			return memberUserId != null && Number(memberUserId) === Number(currentUserId);
		});
	}

	const securityRole = g.securityRole ?? g.security_role;
	if (securityRole != null && currentSecurityRoleId != null) {
		const roleId = parseSecurityRoleIdFromRef(securityRole);
		return roleId != null && Number(roleId) === Number(currentSecurityRoleId);
	}

	return false;
}

export function getAssignmentGroupIdsForUser(
	groups: unknown[],
	currentUserId: number | null,
	currentSecurityRoleId: number | null
): Set<number> {
	const ids = new Set<number>();
	if (currentUserId == null) return ids;

	groups.forEach((group) => {
		if (!isUserMemberOfAssignmentGroup(group, currentUserId, currentSecurityRoleId)) return;
		const groupId = parseGroupIdFromRef(group);
		if (groupId != null) ids.add(groupId);
	});

	return ids;
}

export function isUserInAssignmentGroupById(
	groupId: number | null,
	userGroupIds: ReadonlySet<number> | undefined
): boolean {
	if (groupId == null || !userGroupIds) return false;
	return userGroupIds.has(groupId);
}
